import { useConfig } from "@/context/ConfigContext";
import { useDevice } from "@/context/DeviceContext";
import { useState } from "react";

/**
 * Hook untuk checkout (create-order dan create-payment)
 */
export function useCheckout() {
  const { config, configData } = useConfig();
  const { deviceId } = useDevice();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Create order
   * @param {Object} orderData - Data untuk create order
   * @returns {Promise<Object>} Order response data
   */
  const createOrder = async (orderData) => {
    if (!configData?.team_id || !config?.api?.baseUrl) {
      throw new Error("Config belum siap");
    }

    try {
      setLoading(true);
      setError(null);

      const teamId = configData.team_id;

      const proxyUrl = `/api/checkout?team_id=${teamId}&action=create-order`;

      console.log("[useCheckout] Creating order:", {
        proxyUrl,
        teamId,
        orderData,
      });

      const response = await fetch(proxyUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...(deviceId ? { "X-DEVICE-ID": deviceId } : {}),
        },
        body: JSON.stringify(orderData),
        cache: "no-cache",
      });

      if (!response.ok) {
        let errorData;
        let errorText;

        try {
          errorText = await response.text();
          errorData = JSON.parse(errorText);
        } catch (parseError) {
          errorData = {
            message: errorText || response.statusText,
            status: response.status,
          };
        }

        console.error("[useCheckout] API Error:", errorData);
        throw new Error(
          errorData.message ||
            errorData.error ||
            errorText ||
            response.statusText
        );
      }

      const data = await response.json();
      // API response: { httpCode, status, message, data: { transaction_uuid, transaction_number, ... } }
      const result = data.data != null ? data.data : data;
      return result;
    } catch (err) {
      console.error("[useCheckout] Error creating order:", {
        message: err.message,
        stack: err.stack,
        teamId: configData?.team_id,
      });
      const errorMessage = err.message || "Gagal membuat order";
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Create payment
   * @param {string} transaction_uuid - Transaction UUID dari create order
   * @returns {Promise<Object>} Payment response data
   */
  const createPayment = async (transaction_uuid) => {
    if (!configData?.team_id || !config?.api?.baseUrl) {
      throw new Error("Config belum siap");
    }

    try {
      setLoading(true);
      setError(null);

      const teamId = configData.team_id;

      const proxyUrl = `/api/checkout?team_id=${teamId}&action=create-payment`;

      console.log("[useCheckout] Creating payment:", {
        proxyUrl,
        teamId,
        transaction_uuid,
      });

      const response = await fetch(proxyUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...(deviceId ? { "X-DEVICE-ID": deviceId } : {}),
        },
        body: JSON.stringify({ transaction_uuid }),
        cache: "no-cache",
      });

      if (!response.ok) {
        let errorData;
        let errorText;

        try {
          errorText = await response.text();
          errorData = JSON.parse(errorText);
        } catch (parseError) {
          errorData = {
            message: errorText || response.statusText,
            status: response.status,
          };
        }

        console.error("[useCheckout] API Error:", errorData);
        throw new Error(
          errorData.message ||
            errorData.error ||
            errorText ||
            response.statusText
        );
      }

      const data = await response.json();
      return data.data || data;
    } catch (err) {
      console.error("[useCheckout] Error creating payment:", {
        message: err.message,
        stack: err.stack,
        teamId: configData?.team_id,
      });
      const errorMessage = err.message || "Gagal membuat payment";
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return {
    createOrder,
    createPayment,
    loading,
    error,
  };
}

